import styled from '@emotion/styled';
import { IconButtonGhost } from '@kc-console/common-ui';
import { env, getUserGuidePath } from '@kc-console/context';
import { getHttpStatus, STATUS_CODE, useTranslation } from '@kc-console/core';
import { EmptyState, GuideDocsOutLink } from '@kc-console/shared-ui';
import { IcCloseLargeRegular, IllustStatusForbidden } from '@kep/kcds-icons';

import PanelLayout, { PanelSection } from '../PageLayout/PanelLayout';

interface PanelErrorFallbackProps {
  // throw된 게이팅 에러는 Error 인스턴스가 아닐 수 있다(axios onRejected의 spread 재구성 객체 등).
  // status 분기는 getHttpStatus가 response.status로 처리하므로 원형을 unknown으로 받는다.
  error: unknown;
  // API가 403(권한 없음)을 HTTP status가 아닌 본문 코드로만 표현하는 표면(예: KMS body-code 4104)에서
  // 403과 동일하게 권한 없음 화면을 렌더한다.
  isForbidden?: boolean;
  // API가 200 + 빈 데이터로 미존재를 표현하는 표면(content[0] 패턴)에서 404와 동일하게 not-found 화면을 렌더한다.
  isNotFound?: boolean;
  onClose?: () => void;
}

/**
 * 패널 에러 화면. status별 인플레이스 EmptyState + 경량 닫기 헤더를 제공한다.
 *
 * @description
 * - 화면은 좁은 패널에 맞춰 DataTable의 emptyState와 동일한 컴팩트 EmptyState로 렌더한다.
 *   - 403 / isForbidden(HTTP status 없이 본문 코드로만 오는 권한 없음): 권한 없음 EmptyState(IllustStatusForbidden
 *     + 조회 권한 안내 문구 + 기술 문서 링크). 링크는 DataTable forbidden emptyState와 동일하게
 *     getUserGuidePath(env.KC_FORBIDDEN_GUIDE_LINK)를 사용한다. status 분기는 getHttpStatus에 의존하며 axios
 *     에러에서만 status를 추출한다(비-axios는 null → isForbidden/isNotFound가 없으면 일시 오류 화면).
 *   - 404 / isNotFound(200+empty 미존재): not-found EmptyState(type="warning" 기본 IllustStatusLoadFail로 일시
 *     오류와 동일 look 유지, 문구만 not-found로 교체). 문구 키(error_page.not_found.title /
 *     service_not_found.reason)는 풀페이지 InServiceNotFound와 공유하므로, 향후 패널 전용 문구가 필요하면 키를 분리한다.
 *   - 그 외(5xx 등): 일시 오류 EmptyState(type="warning" — IllustStatusLoadFail).
 *     패널은 목록이 아닌 리소스 상세를 렌더하므로 문구는 상세 기준으로 override한다.
 *     (message=temporary_load_error_detail / guideText=retry_later — 목록 기본 문구·새로고침 버튼 안내는 패널에 부적합).
 * - 패널은 에러 토스트를 띄우지 않는다. 화면 내 EmptyState로만 안내하며, 목록으로 리다이렉트할 수 없으므로
 *   닫기 버튼(onClose)으로만 종료한다. (페이지 종착의 토스트/리다이렉트는 useDetailErrorRedirect가 담당한다.)
 */
export default function PanelErrorFallback({
  error,
  isForbidden = false,
  isNotFound = false,
  onClose,
}: PanelErrorFallbackProps) {
  const { t } = useTranslation();
  const status = getHttpStatus(error);

  const renderErrorState = () => {
    // forbidden(403·body-code)을 not-found보다 먼저 판정해 권한 없음 화면이 not-found로 밀리지 않게 한다.
    if (isForbidden || status === STATUS_CODE.FORBIDDEN) {
      return (
        <EmptyState
          type="warning"
          illustration={<IllustStatusForbidden />}
          message={t('ui:empty_state.forbidden.message')}
          guideText={t('ui:empty_state.forbidden.guide_text')}
          action={<GuideDocsOutLink href={getUserGuidePath(env.KC_FORBIDDEN_GUIDE_LINK)} />}
        />
      );
    }

    if (isNotFound || status === STATUS_CODE.NOT_FOUND) {
      return (
        <EmptyState
          type="warning"
          message={t('ui:error_page.not_found.title')}
          guideText={t('ui:error_page.service_not_found.reason')}
        />
      );
    }

    return (
      <EmptyState
        type="warning"
        message={t('ui:error.temporary_load_error_detail')}
        guideText={t('ui:error.retry_later')}
      />
    );
  };

  return (
    <PanelLayout>
      <PanelSection
        enableScrollObserver={false}
        headerView={<PanelErrorHeader onClose={onClose} />}
      >
        <PanelErrorContent>{renderErrorState()}</PanelErrorContent>
      </PanelSection>
    </PanelLayout>
  );
}

interface PanelErrorHeaderProps {
  onClose?: () => void;
}

/**
 * 닫기 버튼만 갖는 경량 패널 헤더.
 * @description PanelHeader의 selectedItems 등 필수 props 부담을 피하기 위한 fallback 전용 헤더.
 */
const PanelErrorHeader = ({ onClose }: PanelErrorHeaderProps) => {
  const { t } = useTranslation();

  if (!onClose) return null;
  return (
    <HeaderContainer>
      <IconButtonGhost size="sm" aria-label={t('layout:panel.action.close')} onClick={onClose}>
        <IcCloseLargeRegular />
      </IconButtonGhost>
    </HeaderContainer>
  );
};

const HeaderContainer = styled.div`
  display: flex;
  justify-content: flex-end;
  width: 100%;
`;

// 좁은 패널 body에서 EmptyState를 상하좌우 중앙에 배치한다.
const PanelErrorContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: 100%;
`;
